import React, { useState } from 'react'
import { Table, Button } from 'react-bootstrap'
import { useStateValue } from '@State'
import { payments as endpoints } from '@URLS'
import { client } from '@Utils'
import { toast } from '@CommonComponents/toast'

const fmt = amount =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    parseFloat(amount) || 0
  )

const ProgressBar = ({ done, total, current }) => {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  const animating = done < total
  return (
    <div style={{ minWidth: '300px' }}>
      <div className="progress mb-1" style={{ height: '24px' }}>
        <div
          className={`progress-bar progress-bar-striped${animating ? ' progress-bar-animated' : ''}`}
          role="progressbar"
          style={{ width: pct + '%', minWidth: '2.5rem' }}
        >
          {done} / {total}
        </div>
      </div>
      {current && (
        <small className="text-muted">
          {animating ? 'Processing: ' : ''}{current}
        </small>
      )}
    </div>
  )
}

const ProcessButton = ({ activeCount, onClick }) => (
  <Button
    variant="success"
    className="btn-myed btn-success-myed"
    onClick={onClick}
    disabled={activeCount === 0}
  >
    <span className="far fa-check fa-fw mr-2" />
    Process {activeCount} Payments
  </Button>
)

const PreviewTable = () => {
  const [currentState, dispatch] = useStateValue()
  const [progress, setProgress] = useState(null)
  const [completed, setCompleted] = useState(false)

  const rows = currentState.previewRows
  const activeRows = rows
    .map((r, i) => ({ ...r, _rowIndex: i }))
    .filter(r => !r.skip && !r.error)
  const skippedCount = rows.filter(r => r.skip).length
  const errorRows = rows.filter(r => r.error)
  const isProcessing = progress !== null && progress.done < progress.total

  const totalAmount = activeRows.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0)
  const totalRoyalties = activeRows.reduce(
    (sum, r) => sum + r.royalties.reduce((s, ry) => s + parseFloat(ry.amount || 0), 0),
    0
  )
  const totalCommissions = activeRows.reduce(
    (sum, r) => sum + r.commissions.reduce((s, c) => s + parseFloat(c.amount || 0), 0),
    0
  )

  const handleProcess = async () => {
    const total = activeRows.length
    const failed = []
    const results = {}

    setProgress({ done: 0, total, current: activeRows[0]?.title || '', results })

    for (let i = 0; i < activeRows.length; i++) {
      const row = activeRows[i]
      try {
        const resp = await client.post(endpoints.processBatch, {
          description: currentState.description,
          source: currentState.source,
          invoice_number: currentState.invoiceNumber,
          rows: [row],
        })
        const result = resp.data.results && resp.data.results[0]
        if (result && !result.success) {
          failed.push({ myed_coursenum: row.myed_coursenum, error: result.error })
          results[row._rowIndex] = 'error'
        } else {
          results[row._rowIndex] = 'done'
        }
      } catch (err) {
        failed.push({
          myed_coursenum: row.myed_coursenum,
          error:
            (err.response && err.response.data && err.response.data.error) ||
            'Request failed',
        })
        results[row._rowIndex] = 'error'
      }
      setProgress({
        done: i + 1,
        total,
        current: activeRows[i + 1]?.title || '',
        results: { ...results },
      })
    }

    const succeeded = total - failed.length
    failed.forEach(f => toast.error('Failed: ' + f.myed_coursenum + ' — ' + f.error))
    toast.success(
      'Processed ' +
        succeeded +
        ' payment' +
        (succeeded !== 1 ? 's' : '') +
        '.' +
        (skippedCount > 0 ? ' ' + skippedCount + ' skipped.' : '')
    )
    setProgress(null)
    setCompleted(true)
  }

  const actionBar = completed ? (
    <div className="text-right">
      <span className="text-success mr-3">
        <span className="far fa-check-circle mr-1" /> Batch complete
      </span>
      <Button
        className="btn-myed btn-default-myed"
        onClick={() => { dispatch({ type: 'reset' }); setCompleted(false) }}
      >
        Reset
      </Button>
    </div>
  ) : progress ? (
    <ProgressBar done={progress.done} total={progress.total} current={progress.current} />
  ) : (
    <ProcessButton activeCount={activeRows.length} onClick={handleProcess} />
  )

  const rowResult = (rowIndex) => progress && progress.results[rowIndex]

  const statusLabel = (row, rowIndex) => {
    if (row.error) return <span className="label label-warning">Not found</span>
    if (row.skip) return <span className="label label-default">No Enrollments</span>
    const result = rowResult(rowIndex)
    if (result === 'done') return <span className="label label-success">Done</span>
    if (result === 'error') return <span className="label label-danger">Failed</span>
    if (isProcessing) return <span className="label label-default">Pending</span>
    return <span className="label label-success">Ready</span>
  }

  const rowStyle = (row, rowIndex) => {
    if (row.skip) return { opacity: 0.4 }
    if (row.error) return { background: '#fff3cd' }
    const result = rowResult(rowIndex)
    if (result === 'done') return { background: '#d4edda' }
    if (result === 'error') return { background: '#f8d7da' }
    return {}
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <strong>{rows.length} rows</strong>
          {' · '}
          <span className="text-success">{activeRows.length} to process</span>
          {skippedCount > 0 && (
            <span className="text-muted"> · {skippedCount} skipped (no enrollments)</span>
          )}
          {errorRows.length > 0 && (
            <span className="text-danger"> · {errorRows.length} not found</span>
          )}
          {' · '}
          <strong>Total: {fmt(totalAmount)}</strong>
          {totalRoyalties > 0 && (
            <span className="text-muted"> · Royalties: {fmt(totalRoyalties)}</span>
          )}
          {totalCommissions > 0 && (
            <span className="text-muted"> · Commissions: {fmt(totalCommissions)}</span>
          )}
        </div>
        {actionBar}
      </div>

      <Table bordered size="sm">
        <thead className="thead-light">
          <tr>
            <th>Course</th>
            <th>Title</th>
            <th className="text-right">Amount</th>
            <th>Royalties</th>
            <th>Commissions</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={rowStyle(row, i)}>
              <td className="text-nowrap">
                <small className="text-muted d-block">{row.uni_course_num}</small>
                <strong>{row.myed_coursenum}</strong>
              </td>
              <td>{row.title}</td>
              <td className="text-right text-nowrap">
                {row.skip ? '—' : fmt(row.amount)}
              </td>
              <td>
                {row.royalties && row.royalties.length > 0 ? (
                  row.royalties.map((r, j) => (
                    <div key={j} className="text-nowrap">
                      {r.user_full_name} &mdash;{' '}
                      {(parseFloat(r.rate) * 100).toFixed(1)}% &mdash; {fmt(r.amount)}
                    </div>
                  ))
                ) : (
                  <small className="text-muted">&mdash;</small>
                )}
              </td>
              <td>
                {row.commissions && row.commissions.length > 0 ? (
                  row.commissions.map((c, j) => (
                    <div key={j} className="text-nowrap">
                      {c.user_full_name} &mdash;{' '}
                      {(parseFloat(c.rate) * 100).toFixed(1)}% &mdash; {fmt(c.amount)}
                    </div>
                  ))
                ) : (
                  <small className="text-muted">&mdash;</small>
                )}
              </td>
              <td className="text-nowrap">{statusLabel(row, i)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-weight-bold table-active">
            <td colSpan={2}>Totals ({activeRows.length} courses)</td>
            <td className="text-right">{fmt(totalAmount)}</td>
            <td>{fmt(totalRoyalties)}</td>
            <td>{fmt(totalCommissions)}</td>
            <td />
          </tr>
        </tfoot>
      </Table>

      <div className="text-right mt-3 mb-4">
        {actionBar}
      </div>
    </div>
  )
}

export default PreviewTable
